import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import './ShopCollectionResults.scss';

import { focusShopSearch } from '../../Actions/UIActions';
import { editCollection, deleteCollection } from '../../Actions/StoreActions';

import ShopCollectionResult from './Elements/ShopCollectionResult';
import Loader from '../Loader/Loader';
import ShopEmptyResults from '../Curators/ShopEmptyResults';
import SortableList from '../General/SortableList';

import { getSections, isShopper } from '../../Helpers/user_helpers';

const ShopCollectionResults = props => {
  const { isFetchingResults, isFetchingFirstPage, page, hadErrorResults, hasMoreResults, Section_id } = props;

  // Establish Infinite Scroll Observer
  const infiniteScrollElementRef = React.useRef(null);
  const isScrollElementVisible = !!infiniteScrollElementRef.current;
  React.useEffect(() => {
    // If we're already fetching results, don't set up the observer
    if (isFetchingResults || !hasMoreResults || hadErrorResults) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        entry.isIntersecting && entry.isIntersecting && props.setPage(props.page + 1);
      },
      { root: null, rootMargin: '0px', threshold: 0.5 } // 0.5 means when half of the element is visible
    );
    const bottomElement = infiniteScrollElementRef.current;
    if (bottomElement) observer.observe(bottomElement);
    return () => {
      bottomElement && observer.unobserve(bottomElement);
    };
  }, [isFetchingResults, page, hasMoreResults, hadErrorResults, isScrollElementVisible]);

  // Filter the collections to only show the ones in the current section in case of transition
  const currentlyActiveSection =
    (props.isYourShop ? getSections(props.user) : props.sectionResults).find(section => section.id === Section_id) || props.sectionResults[0];
  const currentSectionPreviewType = props.sectionType || currentlyActiveSection?.previewType || 'shelves';

  // Break out the results by column, on resize
  const curNumColumns = currentSectionPreviewType?.includes('post') ? (window.innerWidth > 768 ? 3 : 2) : 1;
  const [numColumns, setNumColumns] = React.useState(curNumColumns);

  React.useEffect(() => {
    setNumColumns(curNumColumns);
  }, [curNumColumns]);

  React.useEffect(() => {
    const handleResize = () => setNumColumns(curNumColumns);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [curNumColumns]);

  // Set columns
  let columns = _.times(numColumns, () => []);
  const sortedCollectionResults = _.orderBy(props.collectionResults, 'sortOrderRank', 'asc');

  const columnChunks = _.chunk(sortedCollectionResults, numColumns);
  columnChunks.forEach((chunk, i) => {
    chunk[0] && columns[0].push(chunk[0]);
    chunk[1] && columns[1].push(chunk[1]);
    chunk[2] && columns[2].push(chunk[2]);
  });

  // Is Empty in general
  const numVisibleCollections = props.collectionResults.filter(collection => collection.total_num_pins || props.isYourShop).length;
  const showEmptyArchiveResults = !isFetchingResults && !numVisibleCollections && props.isYourShop && props.showArchivedCollections;
  const showEmptyShopResults = !isFetchingResults && !numVisibleCollections && props.isYourShop;

  // Empty and yours
  const showEmptySectionResultsForYou = currentlyActiveSection && showEmptyShopResults && props.isYourShop && !isShopper(props.user);

  // Rearrangeable Card For You
  const getCard = (collection, additionalProps = {}) => {
    const { rearrangeHandle } = additionalProps;
    const index = sortedCollectionResults.findIndex(c => c.id === collection.id);
    return (
      <ShopCollectionResult
        key={collection.id}
        columnIdx={Math.floor(index / numColumns)}
        rowIdx={index % numColumns}
        user={props.user}
        shop={props.shop}
        collection={collection}
        previewType={currentSectionPreviewType}
        editCollection={props.editCollection}
        deleteCollection={props.deleteCollection}
        collectionResults={props.collectionResults}
        setCollectionResults={props.setCollectionResults}
        currentAnalytics={props.currentAnalytics}
        isFetchingAnalytics={props.isFetchingAnalytics}
        ui={props.ui}
        rearrangeHandle={rearrangeHandle}
      />
    );
  };
  return (
    <div className={cn('shop-collection-results-outer-container', currentSectionPreviewType)}>
      <div className='shop-collection-results-inner-container'>
        {props.hadErrorResults && (
          <div className='error-message'>
            <div className='error-message-text'>There was an error fetching collections. Please reload the page to try again.</div>
          </div>
        )}
        {isFetchingFirstPage ? (
          <Loader size={64} />
        ) : showEmptySectionResultsForYou ? (
          <ShopEmptyResults shop={props.shop} user={props.user} isYourEmptySection />
        ) : showEmptyArchiveResults ? (
          <ShopEmptyResults shop={props.shop} isEmptyArchive />
        ) : showEmptyShopResults ? (
          <ShopEmptyResults shop={props.shop} clearAllCurators={props.clearAllCurators} isYourEmptyShop focusShopSearch={props.focusShopSearch} />
        ) : (
          <div className={cn('shop-collection-columns', `columns-${numColumns}`, currentSectionPreviewType)}>
            {props.isYourShop ? (
              <SortableList
                isEditing
                containerClassName={cn('shop-collections-sortable-list', `columns-${numColumns}`)}
                items={sortedCollectionResults}
                updateItem={(collection, updates) => {
                  // Local update
                  props.setCollectionResults(props.collectionResults.map(c => (c.id === collection.id ? { ...c, ...updates } : c)));

                  // Remote & Redux update
                  return props.editCollection(collection.id, updates);
                }}
                getCard={getCard}
              />
            ) : (
              columns.map((column, columnIdx) => {
                return (
                  <div key={columnIdx} className='shop-collection-column'>
                    {column.map((collection, rowIdx) => {
                      if (!collection.total_num_pins && !props.isYourShop) return null;
                      return (
                        <ShopCollectionResult
                          key={collection.id}
                          columnIdx={columnIdx}
                          rowIdx={rowIdx}
                          user={props.user}
                          shop={props.shop}
                          collection={collection}
                          previewType={currentSectionPreviewType}
                          editCollection={props.editCollection}
                          deleteCollection={props.deleteCollection}
                          collectionResults={props.collectionResults}
                          setCollectionResults={props.setCollectionResults}
                          currentAnalytics={props.currentAnalytics}
                          isFetchingAnalytics={props.isFetchingAnalytics}
                          ui={props.ui}
                        />
                      );
                    })}
                    {columnIdx === 0 && <div ref={infiniteScrollElementRef} className='observer-trigger' />}
                    {isFetchingResults && !isFetchingFirstPage && (
                      <div className={cn('load-next-page-container', currentSectionPreviewType)}>
                        <Loader size={64} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ShopCollectionResults.propTypes = {
  shop: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  focusShopSearch: PropTypes.func.isRequired,
  editCollection: PropTypes.func.isRequired,
  deleteCollection: PropTypes.func.isRequired,

  // Outside
  isFetchingFirstPage: PropTypes.bool.isRequired,
  isFetchingResults: PropTypes.bool.isRequired,
  collectionResults: PropTypes.array.isRequired,
  sectionResults: PropTypes.array.isRequired,
  Section_id: PropTypes.number,
  setCollectionResults: PropTypes.func.isRequired,
  currentAnalytics: PropTypes.object
};

const mapStateToProps = state => {
  const { shop, user, ui } = state;
  return { shop, user, ui };
};

export default connect(mapStateToProps, {
  focusShopSearch,
  editCollection,
  deleteCollection
})(ShopCollectionResults);
